import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { API } from "../lib/api";
import axios from "axios";
import { CheckCircle2, Loader2, Download, Mail, XCircle, Home } from "lucide-react";
import { Seo } from "../components/Seo";

export default function Success() {
  const [state, setState] = useState("checking"); // checking | paid | failed | timeout
  const [order, setOrder] = useState(null);
  const sessionId = new URLSearchParams(window.location.search).get("session_id");

  const poll = useCallback(async (attempt = 0) => {
    if (!sessionId) { setState("failed"); return; }
    if (attempt >= 8) { setState("timeout"); return; }
    try {
      const res = await axios.get(`${API}/checkout/status/${sessionId}`);
      if (res.data.payment_status === "paid") {
        const o = await axios.get(`${API}/orders/${sessionId}`);
        setOrder(o.data);
        setState("paid");
        return;
      }
      if (res.data.status === "expired") { setState("failed"); return; }
      setTimeout(() => poll(attempt + 1), 2000);
    } catch (e) {
      setTimeout(() => poll(attempt + 1), 2000);
    }
  }, [sessionId]);

  useEffect(() => { poll(0); }, [poll]);

  return (
    <div className="max-w-2xl mx-auto px-6 md:px-12 py-16 md:py-24 text-center">
      <Seo title="Tack för ditt köp" description="Tack för ditt köp hos Enkla Manualer. Här hittar du din nedladdning och kvitto." path="/tack" noindex />
      {state === "checking" && (
        <div data-testid="status-checking">
          <Loader2 className="animate-spin mx-auto text-[#0056D2]" size={56} />
          <h1 className="font-heading text-3xl font-bold text-[#1A1A1A] mt-6">Vi bekräftar din betalning...</h1>
          <p className="text-lg text-[#4A4A4A] mt-3">Det tar bara några sekunder. Vänligen vänta.</p>
        </div>
      )}

      {state === "paid" && order && (
        <div data-testid="status-paid">
          <span className="inline-flex bg-green-50 text-[#0B7A3F] rounded-full p-4">
            <CheckCircle2 size={64} strokeWidth={2} />
          </span>
          <h1 className="font-heading text-4xl font-extrabold text-[#1A1A1A] mt-6">Tack för ditt köp!</h1>
          <p className="text-xl text-[#4A4A4A] mt-4">
            Din manual <strong className="text-[#1A1A1A]">{order.product_title}</strong> är klar.
          </p>

          <a
            href={`${API}/download/${sessionId}`}
            data-testid="download-button"
            className="inline-flex items-center justify-center gap-3 min-h-[64px] px-8 mt-8 bg-[#0056D2] text-white text-xl font-bold rounded-lg hover:bg-[#003E99] transition-colors"
          >
            <Download size={28} strokeWidth={2.5} /> Ladda ner din PDF
          </a>

          <div className="bg-white border-2 border-gray-300 rounded-xl p-6 mt-10 text-left">
            <p className="flex items-center gap-2 font-heading text-xl font-bold text-[#1A1A1A]">
              <Mail size={24} strokeWidth={2.5} /> Vi har även skickat manualen till din e-post
            </p>
            <p className="text-lg text-[#4A4A4A] mt-2">
              En kopia skickas till <strong className="text-[#1A1A1A]">{order.email}</strong>.
              Så här öppnar du PDF:en: tryck på den nedladdade filen så visas den direkt på din skärm.
            </p>
          </div>

          <Link to="/" data-testid="back-home-success" className="inline-flex items-center gap-2 text-lg font-semibold text-[#0056D2] hover:underline mt-8">
            <Home size={22} strokeWidth={2.5} /> Tillbaka till start
          </Link>
        </div>
      )}

      {(state === "failed" || state === "timeout") && (
        <div data-testid="status-failed">
          <span className="inline-flex bg-red-50 text-[#D32F2F] rounded-full p-4">
            <XCircle size={64} strokeWidth={2} />
          </span>
          <h1 className="font-heading text-3xl font-bold text-[#1A1A1A] mt-6">
            {state === "timeout" ? "Betalningen tar längre tid än väntat" : "Betalningen kunde inte slutföras"}
          </h1>
          <p className="text-lg text-[#4A4A4A] mt-3">
            {state === "timeout"
              ? "Kolla din e-post för en bekräftelse, eller kontakta oss om du behöver hjälp."
              : "Ingen betalning genomfördes. Du kan försöka igen."}
          </p>
          <Link to="/" className="inline-flex items-center gap-2 text-lg font-semibold text-[#0056D2] hover:underline mt-8">
            <Home size={22} strokeWidth={2.5} /> Tillbaka till start
          </Link>
        </div>
      )}
    </div>
  );
}
